import React, { useEffect, useMemo, useState } from 'react';
import { X, Scissors } from 'lucide-react';
import {
  getAssessmentById,
  AvailableQuestion,
} from '@/services/PagesServices/AssessmentManagementService';

interface CarveQuestionPickerProps {
  sourceId: number;
  sourceName: string;
  onCancel: () => void;
  onConfirm: (questions: AvailableQuestion[]) => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '40px 16px',
  overflowY: 'auto',
  zIndex: 1000,
};

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  width: '100%',
  maxWidth: 720,
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  overflow: 'hidden',
};

const header: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid #e5e7eb',
  background: '#f8fafc',
};

const body: React.CSSProperties = {
  padding: '16px 20px',
  maxHeight: '60vh',
  overflowY: 'auto',
};

const footer: React.CSSProperties = {
  padding: '12px 20px',
  borderTop: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  background: '#f8fafc',
};

const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  marginBottom: 8,
  cursor: 'pointer',
};

const badge: React.CSSProperties = {
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 999,
  background: '#eef2ff',
  color: '#3730a3',
};

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  color: '#475569',
  border: '1px solid #cbd5e1',
  padding: '8px 14px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 14,
};

const primaryBtn: React.CSSProperties = {
  background: '#1f2937',
  color: '#fff',
  border: 'none',
  padding: '8px 16px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 14,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const CarveQuestionPicker: React.FC<CarveQuestionPickerProps> = ({
  sourceId,
  sourceName,
  onCancel,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<AvailableQuestion[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAssessmentById(sourceId).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) {
        setQuestions(res.data.questions);
        setSelectedIds(new Set(res.data.questions.map((q) => q.id)));
      } else {
        setError(res.error?.message || 'Failed to load questions.');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [sourceId]);

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(questions.map((q) => q.id)));
  const clearAll = () => setSelectedIds(new Set());

  const selectedCount = selectedIds.size;
  const chosen = useMemo(
    () => questions.filter((q) => selectedIds.has(q.id)),
    [questions, selectedIds]
  );

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div style={overlay} onClick={onCancel} role="dialog" aria-modal="true">
      <div style={card} onClick={stop}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Carve from
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>{sourceName}</div>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#475569', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        <div style={body}>
          {loading && <div style={{ color: '#64748b', padding: '20px 0' }}>Loading questions…</div>}
          {error && <div style={{ color: '#b91c1c' }}>{error}</div>}
          {!loading && !error && questions.length === 0 && (
            <div style={{ color: '#94a3b8', padding: '20px 0', textAlign: 'center' }}>
              This assessment has no questions to carve.
            </div>
          )}
          {!loading && !error && questions.length > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13, color: '#475569' }}>
                <span>
                  {selectedCount} of {questions.length} selected
                </span>
                <span>
                  <button type="button" onClick={selectAll} style={{ ...ghostBtn, padding: '4px 10px', marginRight: 6 }}>
                    Select all
                  </button>
                  <button type="button" onClick={clearAll} style={{ ...ghostBtn, padding: '4px 10px' }}>
                    Clear
                  </button>
                </span>
              </div>
              {questions.map((q, idx) => {
                const checked = selectedIds.has(q.id);
                return (
                  <label
                    key={q.id}
                    style={{
                      ...row,
                      background: checked ? '#f0fdf4' : '#fff',
                      borderColor: checked ? '#bbf7d0' : '#e5e7eb',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(q.id)}
                      style={{ marginTop: 4 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                        Q{idx + 1} · <span style={badge}>{q.type}</span> · {q.points} pts
                      </div>
                      <div style={{ fontSize: 14, color: '#0f172a' }}>{q.question}</div>
                    </div>
                  </label>
                );
              })}
            </>
          )}
        </div>

        <div style={footer}>
          <button type="button" style={ghostBtn} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            style={{
              ...primaryBtn,
              opacity: selectedCount === 0 || loading ? 0.5 : 1,
              cursor: selectedCount === 0 || loading ? 'not-allowed' : 'pointer',
            }}
            disabled={selectedCount === 0 || loading}
            onClick={() => onConfirm(chosen)}
          >
            <Scissors size={16} />
            Use {selectedCount} in new assessment
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarveQuestionPicker;
